import React, { useState, useRef, useLayoutEffect } from "react";
import { motion } from "@/motion/appMotion";
import {
  steps,
  howItWorksContent,
} from "../../../data/landingPage/howItWorksContent";
import {
  LANDING_CONTAINER_PADDED,
  LANDING_INNER,
} from "../../../constants/landingPageLayout";
import SectionBadgeChip from "../../common/SectionBadgeChip";
import useLandingMotion from "../../../hooks/useLandingMotion";

const LINE_WIDTH = 3;
const TRACK_COLOR = "#F1F1F1";
const ACTIVE_COLOR = "#0F172A";

// Premium 4-pointed diamond sparkle icon SVG
const SparkleIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2c0 5.5-4.5 10-10 10 5.5 0 10 4.5 10 10 0-5.5 4.5-10 10-10-5.5 0-10-4.5-10-10z" />
  </svg>
);

const HowItWorks = () => {
  const { getProps } = useLandingMotion();
  const [activeStep, setActiveStep] = useState(0);
  const stepsContainerRef = useRef(null);
  const stepRefs = useRef([]);
  const [indicator, setIndicator] = useState({ top: 0, height: 0 });

  useLayoutEffect(() => {
    const updateIndicator = () => {
      const container = stepsContainerRef.current;
      const activeEl = stepRefs.current[activeStep];
      if (!container || !activeEl) return;

      setIndicator({
        top: 0,
        height: activeEl.offsetTop + activeEl.offsetHeight,
      });
    };

    updateIndicator();

    const container = stepsContainerRef.current;
    const resizeObserver =
      container && typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateIndicator)
        : null;

    resizeObserver?.observe(container);
    window.addEventListener("resize", updateIndicator);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updateIndicator);
    };
  }, [activeStep]);

  const mobileSegmentWidth = 100 / steps.length;

  return (
    <section
      id="how-it-works"
      className="relative w-full bg-white py-16 sm:py-20 lg:py-24 clip-x-visible-y"
    >
      <div className={`relative ${LANDING_CONTAINER_PADDED} flex flex-col items-center`}>
        
        {/* Centered Chip and Heading Section */}
        <motion.div
          {...getProps({ duration: 0.6 })}
          className="w-full text-center flex flex-col items-center gap-[6px] mb-12"
        >
          <SectionBadgeChip style={{ width: "158px", height: "32.75px" }}>
            {howItWorksContent.badge.text}
          </SectionBadgeChip>

          {/* Heading */}
          <h2
            className="w-full text-[#0F172A] tracking-tight leading-[54px] text-center"
            style={{
              fontFamily: '"Rethink Sans", sans-serif',
              fontWeight: 600,
              fontSize: "clamp(28px, 4.5vw, 48px)",
            }}
          >
            {howItWorksContent.heading}
          </h2>
        </motion.div>

        {/* Split Screen Content Container */}
        <div className={`${LANDING_INNER} flex flex-col items-center justify-center gap-[50px] lg:flex-row lg:items-stretch lg:gap-[45px]`}>
          
          {/* Left Column: Image Section */}
          <div className="w-full lg:flex-1 flex justify-center items-center">
            <div className="w-full max-w-[680px] aspect-[475/275] flex items-center justify-center">
              <motion.img
                key={activeStep}
                src={steps[activeStep].image}
                alt={steps[activeStep].title}
                className="w-full h-full object-contain"
                decoding="async"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
              />
            </div>
          </div>

          {/* Mobile horizontal progress line */}
          <div
            className="lg:hidden w-full max-w-[398px] h-[3px] rounded-full relative overflow-hidden -my-2"
            style={{ backgroundColor: TRACK_COLOR }}
            aria-hidden
          >
            <motion.div
              className="absolute top-0 left-0 h-full rounded-full"
              style={{ backgroundColor: ACTIVE_COLOR }}
              animate={{ width: `${(activeStep + 1) * mobileSegmentWidth}%` }}
              transition={{ duration: 0.35, ease: "easeInOut" }}
            />
          </div>

          {/* Center vertical indicator (desktop only) */}
          <div
            className="hidden lg:block relative shrink-0 self-stretch"
            style={{ width: LINE_WIDTH }}
            aria-hidden
          >
            <div
              className="absolute inset-0 rounded-full"
              style={{ backgroundColor: TRACK_COLOR, width: LINE_WIDTH }}
            />
            <motion.div
              className="absolute left-0 rounded-full"
              style={{ width: LINE_WIDTH, backgroundColor: ACTIVE_COLOR }}
              animate={{ top: indicator.top, height: indicator.height }}
              transition={{ duration: 0.35, ease: "easeInOut" }}
            />
          </div>

          {/* Right Column: Step Stepper Section */}
          <div
            ref={stepsContainerRef}
            className="w-full lg:w-[398px] lg:shrink-0 flex flex-col gap-[35px]"
          >
            {steps.map((step, index) => {
              const isActive = index === activeStep;

              return (
                <div
                  key={step.title}
                  ref={(el) => {
                    stepRefs.current[index] = el;
                  }}
                  role="button"
                  tabIndex={0}
                  onMouseEnter={() => setActiveStep(index)}
                  onClick={() => setActiveStep(index)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setActiveStep(index);
                    }
                  }}
                  className="flex flex-row items-start gap-[16px] w-full cursor-pointer group"
                >
                  {/* Step Icon Badge */}
                  <div
                    className={`w-[50px] h-[50px] rounded-full shrink-0 flex items-center justify-center transition-all duration-300 ${
                      isActive
                        ? "bg-[#7D3BEE] text-white shadow-lg shadow-indigo-600/10 scale-105"
                        : "border border-slate-200 text-slate-400 bg-white group-hover:border-[#7D3BEE]/50"
                    }`}
                  >
                    <SparkleIcon className="w-5 h-5" />
                  </div>

                  {/* Step Text Content */}
                  <div className="flex flex-col gap-[2px] text-left flex-grow">
                    <h3
                      className={`font-semibold text-[20px] leading-tight tracking-tight transition-colors duration-300 ${
                        isActive ? "text-[#0F172A]" : "text-[#94A3B8]"
                      }`}
                      style={{ fontFamily: '"Rethink Sans", sans-serif' }}
                    >
                      {step.title}
                    </h3>
                    <p
                      className={`text-[14px] leading-relaxed transition-colors duration-300 ${
                        isActive ? "text-[#64748B]" : "text-[#CBD5E1]"
                      }`}
                      style={{ fontFamily: '"Inter", sans-serif' }}
                    >
                      {step.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
